import React from 'react'
import { ComponentExt } from 'util/reactExt'
import { Table, DatePicker, Radio } from 'antd'
import moment from 'moment'
import LineChart from 'containers/shared/LineChart'
import Selector from '../../Selector'

const studyMinsFormatter = min =>
  min ? Math.round((Number(min) / 60) * 100) / 100 : 0

const percentFormatter = percent =>
  percent ? Math.round(Number(percent) * 100 * 100) / 100 : 0

const pkFiledsTextMap = {
  avg_study: {
    my: '我',
    sys: '系统平均学习时长',
    formatter: x => studyMinsFormatter(x),
  },
  avg_articles: {
    my: '我',
    sys: '系统平均学习视频数量',
    formatter: x => x,
  },
  finish_percent: {
    my: '我',
    sys: '系统平均视频完成度',
    formatter: x => percentFormatter(x),
  },
}

interface ICompareWithExampleChartProps {
  entityid?: string | number
}

class ICompareWithExampleChart extends ComponentExt<
  ICompareWithExampleChartProps
> {
  state = {
    myStudyData: [],
    myStudyTrendData: [],
    currentComparison: 'example',
    pkExampleName: undefined,
    pkExampleMoment: moment(),
    pkExampleDate: moment().format('YYYY-MM'),
    pkExampleData: {
      table: { my: {}, sys: {}, other: {} },
      chart: { my: [], sys: [], other: [] },
      msg: { other_name: null },
    },
    currentPkVol: 'avg_study',
  }

  private formatTableData = ({ my, sys, other }) => {
    const nullFormatter = data => data || '无'
    return [
      {
        dimension: '学习时长（小时）',
        my: nullFormatter(studyMinsFormatter(my && my.avg_study)),
        other: nullFormatter(studyMinsFormatter(other && other.avg_study)),
        sys: nullFormatter(studyMinsFormatter(sys && sys.avg_study)),
      },
      {
        dimension: '视频个数',
        my: nullFormatter(my && my.avg_articles),
        other: nullFormatter(other && other.avg_articles),
        sys: nullFormatter(sys && sys.avg_articles),
      },
      {
        dimension: '视频完成度（%）',
        my: nullFormatter(my && percentFormatter(my.finish_percent)),
        other: nullFormatter(other && percentFormatter(other.finish_percent)),
        sys: nullFormatter(sys && percentFormatter(sys.finish_percent)),
      },
    ]
  }

  private formatChartData = ({ my, sys, other }, vol, name) => {
    const currentVol = pkFiledsTextMap[vol]
    const result = my.map((val, key) => ({
      month: val.the_date,
      [currentVol.my]: currentVol.formatter(val[vol]),
      [currentVol.sys]: currentVol.formatter(sys[key][vol]),
    }))

    if (name) {
      result.forEach(
        (item, key) => (item[name] = currentVol.formatter(other[key][vol])),
      )
    }

    return result
  }

  private formatFieldData = (name, vol) => {
    return {
      my: pkFiledsTextMap[vol].my,
      other: name,
      sys: pkFiledsTextMap[vol].sys,
    }
  }

  componentDidMount() {
    this.fetchUserCompareData(undefined, moment().format('YYYY-MM'))
  }

  async fetchUserCompareData(name, date) {
    const data = await this.api.user.getTeamCompareWithExampleData(
      date,
      name,
      this.props.entityid,
    )

    if (typeof data === 'string') {
      return this.$message.warning(data)
    }

    this.setState({
      pkExampleName: name,
      pkExampleData: {
        msg: data.search_msg,
        table: data.total,
        chart: data.month,
      },
    })
  }

  handleSetExampleDate = (m, date) => {
    const pkExampleDate = date
    this.setState({
      pkExampleMoment: m,
      pkExampleDate,
    })
    if (date) {
      this.fetchUserCompareData(this.state.pkExampleName, date)
    }
  }

  handlePkWithExample = name => {
    const { pkExampleDate } = this.state
    this.fetchUserCompareData(name, pkExampleDate)
  }

  handlePkVolChange = e => {
    const currentPkVol = e.target.value
    this.setState({ currentPkVol })
  }

  render() {
    const columns = [
      {
        title: '维度',
        dataIndex: 'dimension',
        className: 'columnCenter',
      },
      {
        title: '我的数据',
        className: 'columnCenter',
        dataIndex: 'my',
      },
      {
        title: `PK对象: ${this.state.pkExampleName || '无'}`,
        className: 'columnCenter',
        dataIndex: 'other',
      },
      {
        title: '系统均值',
        className: 'columnCenter',
        dataIndex: 'sys',
      },
    ]

    const { MonthPicker } = DatePicker
    const { pkExampleData, currentPkVol } = this.state
    const tabs = [
      {
        key: 'avg_study',
        name: '平均时长',
      },
      { key: 'avg_articles', name: '视频学习数量' },
      { key: 'finish_percent', name: '系统平均视频完成度(%)' },
    ]

    const tableData = this.formatTableData(pkExampleData.table)
    const chartData = this.formatChartData(
      pkExampleData.chart,
      currentPkVol,
      pkExampleData.msg.other_name,
    )
    const fieldData = this.formatFieldData(
      pkExampleData.msg.other_name,
      currentPkVol,
    )

    return (
      <section>
        <h4
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '27px 0',
          }}
        >
          <Selector onSubmit={this.handlePkWithExample} />
          <MonthPicker
            placeholder={'请选择日期'}
            defaultValue={moment()}
            style={{ marginRight: 16 }}
            value={this.state.pkExampleMoment}
            disabledDate={current =>
              (current && current > moment().endOf('day')) ||
              current < moment('2018-01-01')
            }
            onChange={(m, date) => this.handleSetExampleDate(m, date)}
          />
        </h4>
        <Table
          pagination={false}
          columns={columns}
          dataSource={tableData}
          bordered
        />
        <h4 style={{ display: 'flex', alignItems: 'center', height: 88 }}>
          <span
            style={{
              fontSize: 16,
              color: '#333',
              marginRight: 20,
            }}
          >
            榜样重点数据对比
          </span>

          <Radio.Group
            onChange={this.handlePkVolChange}
            value={this.state.currentPkVol}
            style={{ marginBottom: 8 }}
          >
            {tabs.map(t => (
              <Radio.Button key={t.key} value={t.key}>
                {t.name}
              </Radio.Button>
            ))}
          </Radio.Group>
        </h4>
        <LineChart data={chartData} filedsTextMap={fieldData} />
      </section>
    )
  }
}

export default ICompareWithExampleChart
